import React, { useContext, useState, useMemo, useCallback } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import { SVGIcon } from '@superops/super-icons';
import {
  Heading,
  ButtonGroup,
  Button,
  Spinner,
  Link,
  Popover,
  ResourceItem,
  Badge,
  Modal
} from '@superops/ui-design-system';
import {
  useKbContext,
  CreateCollection,
  KB_CONSTANTS,
  KB_ROUTE
} from '@nile/rmm/src/modules/knowledge-base';
import { getUpdateDifferencePayload } from '@nile/rmm/src/modules/knowledge-base/helper';
import {
  CREATE_ARTICLE_ID,
  KB_ARTICLE_MODULE
} from '@nile/rmm/src/modules/knowledge-base/helper/constants';
import TabContext from '@nile/mangifera/src/Contexts/TicketContext';
import ImportArticle from '@nile/rmm/src/modules/knowledge-base/components/ImportArticle';
import {
  COMMON_CONSTANTS,
  getTranslatedText,
  toast,
  useProduct,
  EventEmitter,
  useIsAuthorisedHook,
  getHeaderValue,
  fetchApi,
  useActivityTracker,
  AccessControl,
  DeleteModal
} from '@nile/components';
import MoveArticle from '../../subpages/article-page/MoveArticle';
import ArticleVisibility from '../../subpages/article-page/ArticleVisibility';
import envConfig from '@nile/home/src/Config/env';
import {
  BULK_UPDATE_ARTICLE_MOVE,
  BULK_UPDATE_ARTICLE_VISIBILITY,
  DELETE_COLLECTION
} from '../../graphql';
import { AccountClient } from '@nile/graphql';
import { isEmpty } from 'lodash';
import { removeItemFromTree } from '../../components/helper';
import { useFlags } from 'launchdarkly-react-client-sdk';

let broadcastChannel;
if (BroadcastChannel) {
  broadcastChannel = new BroadcastChannel('SuperShare');
}

function KbToolbar(props) {
  const [
    createCollectionToggleStatus,
    setCreateCollectionToggleStatus
  ] = useState(false);
  const tabContext = useContext(TabContext);
  const {
    activeViewConfig,
    bulkRecord,
    filterConditions,
    setbulkRecord,
    showImportSummary,
    setShowImportSummary,
    loadingImportSummary,
    treeItems,
    setTreeItems
  } = useKbContext();
  const { source, data: sourceData } = activeViewConfig;
  const { toggleSidebar, intl, history } = props;
  const flags = useFlags();
  const [popoverActive, setPopoverActive] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editCollectionData, setEditCollectionData] = useState(null);
  const [importArticle, setImportArticle] = useState(false);
  const [importInProgress, setImportInProgress] = useState(false);
  const isKbAddAuthorised = useIsAuthorisedHook('knowledgeBase.add');
  const isKbDeleteAuthorised = useIsAuthorisedHook('knowledgeBase.delete');
  const isKbEditAuthorised = useIsAuthorisedHook('knowledgeBase.edit');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const sidebarChange = () => {
    toggleSidebar();
  };

  const openNewArticle = () => {
    tabContext.openTab({
      module: KB_ARTICLE_MODULE,
      url: `/kb/article/${CREATE_ARTICLE_ID}/create`,
      data: {
        updateData: true,
        sourceData,
        id: CREATE_ARTICLE_ID
      }
    });
  };

  const togglePopoverActive = useCallback(() => {
    setPopoverActive((popoverActive) => !popoverActive);
  });

  const activator = (
    <div className="ml8">
      <Button
        onClick={togglePopoverActive}
        plain
        monochrome
        icon={
          <Badge
            size="small"
            radial
            icon={<SVGIcon name="Actions" size="md" />}
          />
        }
      />
    </div>
  );

  const modalActivator = (
    <div className="pointer flex center actr g5 danger">
      <SVGIcon name="Delete" color="#E02020" />
      <FormattedMessage id="app.kb.delete.collection.label" />
    </div>
  );

  const handleEditCollection = (collectionData) => {
    setEditCollectionData(collectionData);
    setIsEditMode(true);
    setCreateCollectionToggleStatus(true);
  };

  const deleteKbCollection = async (itemId) => {
    try {
      const res = await AccountClient.mutate({
        mutation: DELETE_COLLECTION,
        variables: {
          input: {
            kbCollection: { itemId }
          }
        }
      });
      if (res?.data?.deleteKbCollection) {
        const {
          tree: updatedTree,
          selectId: collectionToBeSelected
        } = removeItemFromTree(treeItems, itemId, false);
        setTreeItems(updatedTree);
        useActivityTracker({
          event: 'KB_DELETE_COLLECTION - Collection Deleted'
        });
        toast('success', 'app.kb.delete.collection.success');
        if (collectionToBeSelected) {
          history.push(`${KB_ROUTE.COLLECTION_VIEW}/${collectionToBeSelected}`);
        }
      }
    } catch (error) {
      toast('error', 'error.page.config.default.title');
    }
  };

  const resetHandleData = () => {
    setIsEditMode(false);
    setEditCollectionData(null);
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    await deleteKbCollection(activeViewConfig.data.itemId);
    setDeleteLoading(false);
    setShowDeleteModal(false);
  };

  const BulkActionToolbar = () => {
    const [loading, setLoading] = useState({
      load: false,
      action: null
    });
    const isITProduct = useProduct(COMMON_CONSTANTS.IT);
    const bulkMoveArticle = (val) => {
      useActivityTracker({
        event: 'KB_ARTICLE_BULK_MOVE'
      });
      setLoading({
        load: true,
        action: 'move'
      });
      AccountClient.mutate({
        mutation: BULK_UPDATE_ARTICLE_MOVE,
        variables: {
          input: {
            articles: bulkRecord.map((article) => ({
              itemId: article.itemId
            })),
            parent: val.parent
          }
        }
      })
        .then((res) => {
          if (res.data.moveArticles) {
            setbulkRecord([]);
            toast('success', 'common.app.kb.article.updated.succesfully');
            EventEmitter.dispatch(KB_CONSTANTS.KB_LIST_TABLE_REFETCH_EVENT);
          }
        })
        .finally(() => {
          setLoading({
            load: false,
            action: null
          });
        });
    };
    const bulkVisibilityChange = (val) => {
      useActivityTracker({
        event: 'KB_ARTICLE_BULK_VISIBILITY_CHANGE'
      });
      setLoading({
        load: true,
        action: 'visibility'
      });
      AccountClient.mutate({
        mutation: BULK_UPDATE_ARTICLE_VISIBILITY,
        variables: {
          input: {
            articles: bulkRecord
              .filter((article) => article.status === 'DRAFT')
              .map((article) => ({
                itemId: article.itemId
              })),
            loginRequired: val.loginRequired,
            visibility: getUpdateDifferencePayload(
              [],
              val.visibility,
              isITProduct
            )
          }
        }
      })
        .then((res) => {
          if (res.data.publishArticles) {
            setbulkRecord([]);
            toast('success', 'common.app.kb.article.updated.succesfully');
            EventEmitter.dispatch(KB_CONSTANTS.KB_LIST_TABLE_REFETCH_EVENT);
          }
        })
        .finally(() => {
          setLoading({
            load: false,
            action: null
          });
        });
    };

    if (!isKbEditAuthorised) return <></>;

    const articlesWithDraft = bulkRecord.find(
      (article) => article.status === 'DRAFT'
    );

    return (
      <ButtonGroup>
        <Button outline selected>
          {`${bulkRecord?.length} ${getTranslatedText(
            intl,
            'ticket.details.selected'
          )}`}
        </Button>
        <MoveArticle
          activator={
            <Button
              icon={<SVGIcon name="MoveToFolder" />}
              loading={loading.load && loading.action === 'move'}
              disabled={loading.load}
            >
              <FormattedMessage id="mail.client.conversation.button" />
            </Button>
          }
          article={{
            ...(bulkRecord?.[0] || {}),
            parent: sourceData?.isViews ? null : bulkRecord?.[0]?.parent
          }}
          onMove={bulkMoveArticle}
        />
        {articlesWithDraft && (
          <ArticleVisibility
            article={{
              loginRequired: true
            }}
            activator={
              <Button
                primary
                onClick={() => {}}
                icon={<SVGIcon name="Share" />}
                loading={loading.load && loading.action === 'visibility'}
                disabled={loading.load}
              >
                <FormattedMessage id="admin.policyMgmn.publish" />
              </Button>
            }
            onVisibilityChange={bulkVisibilityChange}
          />
        )}
      </ButtonGroup>
    );
  };

  const MemoizedBulkActionToolbar = React.memo(BulkActionToolbar);

  const fetchLastImport = () => {
    setImportInProgress(true);
    const importSummaryApi = `${envConfig.loginRoot}/import/lastImport?module=${KB_CONSTANTS.KB_ARTICLE}`;
    const requestOptions = {
      method: 'GET',
      credentials: 'include',
      headers: {
        ...getHeaderValue(),
        'Content-Type': 'application/json'
      }
    };

    fetchApi(importSummaryApi, requestOptions)
      .then((res) => res.json())
      .then((res) => {
        if (isEmpty(res?.data) || res?.data?.status === 'SUCCESS') {
          setImportArticle(true);
        } else {
          toast('info', 'app.integrations.pax8.syncInProgress.title');
        }
        setImportInProgress(false);
      })
      .catch(() => {
        setImportInProgress(false);
      });
  };

  return (
    <>
      <div className="h90px p20 white-bg divider">
        <div className="flex actr full-width fx-sb">
          <div className="flex flex-center">
            {toggleSidebar && (
              <div className="flex-item-fixed" onClick={sidebarChange}>
                <div className="mr16 navMenu">
                  <Button icon={<SVGIcon name="CustomViewsPanel" />} />
                </div>
              </div>
            )}
            <div className="flex visibilityContainer g10 actr">
              <Heading>
                <div className="ellipsis maxW460">
                  {source === KB_CONSTANTS.SOURCE_COLLECTION_TREE
                    ? sourceData?.name
                    : sourceData?.selectedViewName}
                </div>
              </Heading>
              {source === KB_CONSTANTS.SOURCE_COLLECTION_TREE &&
                (isKbEditAuthorised || isKbDeleteAuthorised) && (
                  <Popover
                    active={popoverActive}
                    activator={activator}
                    preferredAlignment="left"
                    onClose={() => {
                      togglePopoverActive();
                    }}
                  >
                    <div className="min-width-150 flex col no-scroll">
                      <AccessControl
                        roleToCheck="knowledgeBase.edit"
                        render={
                          <ResourceItem
                            verticalAlignment="center"
                            gutter="md"
                            onClick={() => {
                              togglePopoverActive();
                              handleEditCollection(activeViewConfig);
                            }}
                          >
                            <div className="flex actr g5">
                              <SVGIcon name="Edit" />
                              <FormattedMessage id="app.kb.rename.collection" />
                            </div>
                          </ResourceItem>
                        }
                      />

                      <AccessControl
                        roleToCheck="knowledgeBase.delete"
                        render={
                          <ResourceItem verticalAlignment="center" gutter="md">
                            <div onClick={() => setShowDeleteModal(true)}>
                              {modalActivator}
                            </div>
                          </ResourceItem>
                        }
                      />
                    </div>
                  </Popover>
                )}
            </div>
          </div>
          <div>
            {bulkRecord.length === 0 && (
              <ButtonGroup>
                {filterConditions?.condition && (
                  <div className="ml8 mr8">
                    <Button
                      plain
                      onClick={() => filterConditions?.resetFilter()}
                    >
                      <FormattedMessage id="ticket.details.resetFilters" />
                    </Button>
                  </div>
                )}
                {source === KB_CONSTANTS.SOURCE_COLLECTION_TREE &&
                  isKbAddAuthorised && (
                    <div className="ml8 mr8">
                      <Link
                        onClick={() => {
                          if (!showImportSummary) {
                            setShowImportSummary(true);
                          }
                        }}
                      >
                        {loadingImportSummary ? (
                          <div className="fx actr g5">
                            <div className="fx actr mbm-4">
                              <Spinner size="small" />
                            </div>
                            {getTranslatedText(
                              intl,
                              'app.kb.last.import.loading'
                            )}
                            ...
                          </div>
                        ) : (
                          getTranslatedText(
                            intl,
                            'common.app.kb.last.import.label'
                          )
                        )}
                      </Link>
                    </div>
                  )}
                {source === KB_CONSTANTS.SOURCE_COLLECTION_TREE &&
                  isKbAddAuthorised &&
                  !sourceData?.parent && (
                    <Button
                      onClick={() => setCreateCollectionToggleStatus(true)}
                      icon={<SVGIcon name="AddFolder" />}
                    >
                      <FormattedMessage id="common.app.kb.sub.collection.label" />
                    </Button>
                  )}

                {isKbAddAuthorised && (
                  <Button
                    onClick={fetchLastImport}
                    loading={importInProgress}
                    icon={<SVGIcon name="Import" />}
                  >
                    <FormattedMessage id="common.import" />
                  </Button>
                )}

                {isKbAddAuthorised && (
                  <Button
                    primary
                    onClick={() => openNewArticle()}
                    icon={<SVGIcon name="Add" />}
                  >
                    <FormattedMessage id="app.kb.new.article.label" />
                  </Button>
                )}
              </ButtonGroup>
            )}
            {useMemo(
              () => bulkRecord.length > 0 && <MemoizedBulkActionToolbar />,
              [bulkRecord.length]
            )}
          </div>
        </div>
      </div>
      {importArticle && (
        <ImportArticle
          onClose={() => setImportArticle(false)}
          article={sourceData}
        />
      )}
      <CreateCollection
        toggleStatus={createCollectionToggleStatus}
        updateStatus={setCreateCollectionToggleStatus}
        isSubCollection={isEditMode ? sourceData?.parent !== null : true} //Subcollection identifier
        isEditMode={isEditMode}
        initialData={editCollectionData}
        resetHandleData={resetHandleData}
      />
      {showDeleteModal && (
        <Modal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title={getTranslatedText(intl, 'common.warning')}
          variant="confirmation"
          iconSize="xl"
          primaryAction={{
            content: getTranslatedText(
              intl,
              'app.confirmationModal.delete.btn'
            ),
            onAction: () => {
              togglePopoverActive();
              handleDelete();
            },
            destructive: true,
            loading: deleteLoading
          }}
          secondaryActions={[
            {
              content: getTranslatedText(intl, 'common.cancel'),
              onAction: () => setShowDeleteModal(false),
              disabled: deleteLoading
            }
          ]}
        >
          <div className="mbt10">
            {getTranslatedText(
              intl,
              'app.kb.delete.collection.warning.message'
            )}
          </div>
        </Modal>
      )}
    </>
  );
}

export default injectIntl(withRouter(KbToolbar));
